import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Col,
  Container,
  Row,
  Card,
  CardBody,
  Input,
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
} from "reactstrap";

import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import BreadCrumb from "Components/Common/BreadCrumb";
import Loader from "Components/Common/Loader";
import { useFormik } from "formik";
import * as Yup from "yup";

import type { RootState } from "store";
import type { ThunkDispatch } from "redux-thunk";
import type { AnyAction } from "redux";
import GoalContainer from "./goalcontainer";
import { handleValidDate, handleValidTime } from "helpers/common_helper";

import NewDeleteModal from "Components/Common/NewDeleteModal";
import { toast } from "react-toastify";

import {
  fetchGoalCategoryAdmin,
  addGoalCategoryAdmin,
  updateGoalCategoryAdmin,
  deleteGoalCategoryAdmin,
} from "slices/goal/thunk";
import GoalCategoryForm from "./goalcategoryform";

import { Link, Navigate, useNavigate } from "react-router-dom";

const selectGoalState = (state: RootState) => (state as any).goalcategory;

const goalSelector = createSelector(selectGoalState, (goal) => ({
  categories: goal.categories,
  loading: goal.loading,
}));

const GoalPage: React.FC = () => {
  const dispatch: ThunkDispatch<RootState, any, AnyAction> = useDispatch();
  const { categories, loading } = useSelector(goalSelector);

  const goalList = categories || [];

  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editGoalData, setEditGoalData] = useState<any>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedGoalId, setSelectedGoalId] = useState<number | null>(null);

  const loadGoalList = useCallback(() => {
    return dispatch(fetchGoalCategoryAdmin());
  }, [dispatch]);

  useEffect(() => {
    loadGoalList();
  }, [loadGoalList]);

  const navigate = useNavigate();

  const handleDeleteClick = (id: number) => {
    setSelectedGoalId(id);
    setDeleteModalOpen(true);
  };

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        category_name: Yup.string()
          .required("Category name is required")
          .matches(/^[a-zA-Z _-]*$/, "Only alphabets, spaces, hyphens (-) and underscores (_) are allowed")
          .max(70, "Maximum 70 characters allowed"),
        category_order: Yup.number()
          .required("Category order is required")
          .min(1, "Category order must be at least 1")
          .integer("Category order must be a whole number"),
        status: isEdit
          ? Yup.string().required("Status is required")
          : Yup.string().optional(),
      }),
    [isEdit]
  );

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      category_name: "",
      category_order: "",
      status: "",
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      // Close modal immediately
      setIsModalOpen(false);
      resetForm();
      const editData = editGoalData;
      const isEditMode = isEdit;
      setIsEdit(false);
      setEditGoalData(null);

      try {
        if (isEditMode && editData) {
          const res = await dispatch(
            updateGoalCategoryAdmin(editData.id, {
              category_name: values.category_name,
              category_order: Number(values.category_order),
              status: values.status || "",
            })
          );
          if (!res?.success) {
            toast.error(res?.message || "Failed to update goal category");
            return;
          }
          toast.success("Goal category updated successfully!");
        } else {
          const res = await dispatch(
            addGoalCategoryAdmin({
              category_name: values.category_name,
              category_order: Number(values.category_order),
            })
          );
          if (!res?.success) {
            toast.error(res?.message || "Failed to add goal category");
            return;
          }
          toast.success("Goal category added successfully!");
        }

        await dispatch(fetchGoalCategoryAdmin());
      } catch (err) {
        console.error("Error saving goal category:", err);
        toast.error("An error occurred. Please try again.");
      }
    },
  });

  const toggleModal = () => {
    if (!isModalOpen) {
      validation.resetForm();
      setIsEdit(false);
      setEditGoalData(null);
    }
    setIsModalOpen(!isModalOpen);
  };

  const handleRowClick = useCallback((row: any) => {}, []);

  const handleEditClick = useCallback(
    (row: any) => {
      setIsEdit(true);
      setEditGoalData(row);
      setIsModalOpen(true);

      validation.setValues({
        category_name: row.category_name || "",
        category_order: row.category_order || "",
        status: row.status || "",
      });
    },
    [validation]
  );

  const filteredGoals = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return goalList;
    return goalList.filter((t: any) =>
      t.category_name?.toLowerCase().includes(q)
    );
  }, [goalList, searchQuery]);

  const columns = useMemo(
    () => [
      {
        header: "Category ID",
        accessorKey: "id",
        cell: ({ row }: any) => {
          const id = row.original.id;
          return `#ID${String(id).padStart(3, "0")}`;
        },
      },
      { header: "Category Name", accessorKey: "category_name" },
      {
        header: () => <div style={{ textAlign: "center" }}> Category Order</div>,
        accessorKey: "category_order",
        cell: ({ getValue }: any) => (
          <div style={{ textAlign: "center" }}>{getValue()}</div>
        ),
      },
      {
        header: "Created On",
        accessorKey: "createdon",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const value = cell.getValue();
          return value ? (
            <>
              {handleValidDate(value)},{" "}
              <small className="text-muted">{handleValidTime(value)}</small>
            </>
          ) : (
            <>N/A</>
          );
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        cell: (cell: any) => {
          const value = cell.getValue();
          const lowerVal = value?.toString().toLowerCase();
          const statusColor =
            lowerVal === "active" ? "#159E3A" : "#A65F00";

          return (
            <span
              style={{
                display: "inline-block",
                padding: "4px 12px",
                borderRadius: "20px",
                fontSize: "12px",
                fontWeight: "500",
                color: statusColor,
                border: `1px solid ${statusColor}`,
                textTransform: "capitalize",
              }}
            >
              {value}
            </span>
          );
        },
      },
      { header: "Created By", accessorKey: "createdby" },
      {
        header: "Actions",
        accessorKey: "actions",
        enableColumnFilter: false,
        cell: (cellProps: any) => {
          const row = cellProps.row.original;
  
          return (
            <ul className="list-inline hstack gap-1 mb-0">
              <li className="list-inline-item" title="Edit">
                <Button
                  color=""
                  className="btn btn-icon btn-soft-primary btn-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditClick(row);
                  }}
                >
                  <i className="ri-pencil-fill"></i>
                </Button>
              </li>
  
              {/* Delete */}
             <li className="list-inline-item" title="Delete">
              <Button
                color=""
                className="btn btn-icon btn-soft-danger btn-sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteClick(row.id);  
                }}
              >
                <i className="ri-delete-bin-line"></i>
              </Button>
            </li>
            </ul>
          );
        },
      },
    ],
    [handleEditClick, handleDeleteClick]
  );

  return (
    <div className="page-content">
      <Container fluid className="border pt-4 rounded-4 bg-light mb-3 ms-3 pe-4">
        <BreadCrumb title="Goal Category" pageTitle={["Goal", "Categories"]} />

        <Row className="align-items-center g-3 d-flex justify-content-between pb-4 pe-3">
          <Col md={3}>
            {/* <div className="position-relative">
              <Input
                type="text"
                className="form-control border ps-5 ms-2 rounded-3"
                value={searchQuery}
                placeholder="Search Category..."
                onChange={(e) => setSearchQuery(e.target.value)}
              /> */}
              {/* <i
                className="ri-search-line position-absolute top-50 start-0 translate-middle-y text-muted ms-3"
                style={{ fontSize: "1.1rem" }}
              />
            </div> */}
          </Col>
          <Col md={9} className="text-end">
          <Button color="primary" onClick={toggleModal}>
          + Add New
        </Button>  
          </Col>
        </Row>

        <Row>
          <Col lg={12}>
            <Card className="border-0 shadow-none">
              <CardBody>
                {!loading ? (
                  filteredGoals.length > 0 ? (
                    <GoalContainer
                      columns={columns}
                      data={filteredGoals}
                      customPageSize={10000}
                      limit={10000}
                      setPage={() => {}}
                      onRowClick={handleRowClick}
                      tableClass="align-middle border-bottom rounded-pill"
                      theadClass="bg-primary text-white"
                      divClass="table-responsive table-card"
                    />
                  ) : (
                    <div className="text-center p-4">
                      No records found.
                    </div>
                  )
                ) : (
                  <Loader />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Add/Edit Modal */}
      <Modal isOpen={isModalOpen} toggle={toggleModal} centered size="lg">
        <ModalHeader toggle={toggleModal}>
          {isEdit ? "Edit Goal Category" : "Add New Goal Category"}
        </ModalHeader>
        <form onSubmit={validation.handleSubmit}>
          <ModalBody>
            <GoalCategoryForm validation={validation} isEdit={isEdit} />
          </ModalBody>
          <ModalFooter>
            <Button
              type="submit"
              color="primary"
            >
              {isEdit ? "Update" : "Add"}
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <NewDeleteModal
        show={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete Goal Category"
        message="Are you sure you want to delete this goal category?"
        onConfirm={async () => {
          if (selectedGoalId) {
            setDeleteModalOpen(false);
            const categoryId = selectedGoalId;
            setSelectedGoalId(null);

            const res = await dispatch(deleteGoalCategoryAdmin(categoryId));
            if (res?.success) {
              toast.success("Goal category deleted successfully!");
              await dispatch(fetchGoalCategoryAdmin());
            } else {
              toast.error(res?.message || "Failed to delete goal category");
            }
          }
        }}
      />
    </div>
  );
};

export default GoalPage;
